import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/store/auth';
import { logger } from '@/utils/logger';

interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  connectionError: string | null;
  emit: (event: string, data?: any) => void;
  on: (event: string, callback: (data: any) => void) => void;
  off: (event: string, callback?: (data: any) => void) => void;
  joinRoom: (room: string) => void;
  leaveRoom: (room: string) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  
  const { isAuthenticated, tokens, user } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated && tokens?.accessToken && user) {
      // WebSocket 연결 실패로 인한 앱 블록킹을 방지하기 위한 타임아웃
      let connectionTimeout: NodeJS.Timeout;
      
      // Create socket connection
      const newSocket = io(import.meta.env.VITE_WS_URL || 'ws://localhost:3002', {
        auth: {
          token: tokens.accessToken,
        },
        transports: ['websocket', 'polling'],
        timeout: 5000, // 연결 타임아웃을 5초로 단축
        reconnection: true,
        reconnectionAttempts: 3, // 재연결 시도 횟수 줄임
        reconnectionDelay: 1000,
        reconnectionDelayMax: 3000,
        forceNew: true, // 새로고침 시 새 연결 강제
      });

      // 최대 10초 후 연결 포기 (앱이 블록되지 않도록)
      connectionTimeout = setTimeout(() => {
        if (!isConnected) {
          logger.warn('WebSocket connection timeout - proceeding without real-time features');
          setConnectionError('실시간 기능을 일시적으로 사용할 수 없습니다');
          newSocket.disconnect();
        }
      }, 10000);

      // Connection event handlers
      newSocket.on('connect', () => {
        clearTimeout(connectionTimeout);
        setIsConnected(true);
        setConnectionError(null);
        logger.info('WebSocket connected', {
          socketId: newSocket.id,
          userId: user.id,
        });
      });

      newSocket.on('disconnect', (reason) => {
        setIsConnected(false);
        logger.info('WebSocket disconnected', {
          reason,
          userId: user.id,
        });
        
        // 자동 재연결이 실패할 경우 포기
        setTimeout(() => {
          if (!isConnected && newSocket.connected === false) {
            logger.info('WebSocket auto-reconnection timeout - giving up');
          }
        }, 15000);
      });

      newSocket.on('connect_error', (error) => {
        setConnectionError('실시간 기능 연결에 실패했습니다');
        logger.warn('WebSocket connection error - app continues normally', error.message);
        // WebSocket 연결 실패는 앱 동작을 차단하지 않음
      });

      newSocket.on('error', (error) => {
        logger.warn('WebSocket error', error);
        // WebSocket 에러는 경고만 표시
      });

      newSocket.on('reconnect', (attemptNumber) => {
        logger.info('WebSocket reconnected after', attemptNumber, 'attempts');
        setIsConnected(true);
        setConnectionError(null);
      });

      newSocket.on('reconnect_attempt', (attemptNumber) => {
        logger.debug('WebSocket reconnection attempt', attemptNumber);
      });

      newSocket.on('reconnect_failed', () => {
        logger.warn('WebSocket reconnection failed - app will continue without real-time features');
        setConnectionError('실시간 기능을 사용할 수 없습니다');
      });

      // Handle welcome message
      newSocket.on('connected', (data) => {
        logger.info('WebSocket welcome message received', data);
      });

      // Handle presence updates
      newSocket.on('presence:user_status', (data) => {
        logger.debug('User presence update', data);
        // You can dispatch this to a global presence store if needed
      });

      // Handle system notifications
      newSocket.on('system:notification', (data) => {
        logger.info('System notification received', data);
        // You can show toast notifications here
      });

      // Handle system maintenance notifications
      newSocket.on('system:maintenance', (data) => {
        logger.warn('Maintenance notification', data);
        // You can show maintenance banners here
      });

      setSocket(newSocket);

      // Cleanup on unmount or auth change
      return () => {
        clearTimeout(connectionTimeout);
        if (newSocket) {
          newSocket.disconnect();
          setSocket(null);
          setIsConnected(false);
        }
      };
    } else if (socket) {
      // User logged out, disconnect socket
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
      setConnectionError(null);
    }
  }, [isAuthenticated, tokens?.accessToken, user]);

  // Helper methods
  const emit = (event: string, data?: any) => {
    if (socket && isConnected) {
      socket.emit(event, data);
      logger.debug('WebSocket event emitted', { event, data });
    } else {
      logger.debug('WebSocket not connected - skipping event', { event });
      // WebSocket이 연결되지 않아도 앱은 계속 작동
    }
  };

  const on = (event: string, callback: (data: any) => void) => {
    if (socket) {
      socket.on(event, callback);
    }
  };

  const off = (event: string, callback?: (data: any) => void) => {
    if (socket) {
      if (callback) {
        socket.off(event, callback);
      } else {
        socket.off(event);
      }
    }
  };

  const joinRoom = (room: string) => {
    if (socket && isConnected) {
      emit('join:room', room);
      logger.debug('Joined WebSocket room', { room });
    }
  };

  const leaveRoom = (room: string) => {
    if (socket && isConnected) {
      emit('leave:room', room);
      logger.debug('Left WebSocket room', { room });
    }
  };

  const contextValue: WebSocketContextType = {
    socket,
    isConnected,
    connectionError,
    emit,
    on,
    off,
    joinRoom,
    leaveRoom,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  
  if (context === undefined) {
    // WebSocketProvider가 없어도 기본값 반환 (앱이 작동하도록)
    return {
      socket: null,
      isConnected: false,
      connectionError: 'WebSocket not initialized',
      emit: () => logger.debug('WebSocket not available'),
      on: () => {},
      off: () => {},
      joinRoom: () => {},
      leaveRoom: () => {},
    };
  }
  
  return context;
};